import type { Filter } from "../../../../components/FilterChips/FilterChips";

import { getNonDismissibleChipIds, resolveStatus } from "../../../../utils/statusFilter";

import { type CompetenciesFilter, LEVEL_GAP_LABELS, SOURCE_LABELS, STATUS_LABELS } from "./schema";

// Chip order mirrors the filter panel's field order so the two surfaces read the same.
export const convertFiltersToFilterChips = (filters: CompetenciesFilter): Filter[] => {
	const chips: Filter[] = [];

	if (filters.competency) {
		chips.push({ id: "competency", type: "string", label: "Competency", value: filters.competency.name });
	}

	if (filters.competencies?.length) {
		chips.push({
			id: "competencies",
			type: "string",
			label: "Competency",
			value: filters.competencies.map((competency) => competency.name).join(", "),
		});
	}

	if (filters.source && filters.source !== "any") {
		chips.push({ id: "source", type: "string", label: "Type", value: SOURCE_LABELS[filters.source] });
	}

	if (filters.assignee) {
		chips.push({
			id: "assignee",
			type: "single-employee",
			label: "Assignee",
			value: { id: filters.assignee.id, name: filters.assignee.name },
		});
	}

	if (filters.manager) {
		chips.push({
			id: "manager",
			type: "single-employee",
			label: "Manager",
			value: { id: filters.manager.id, name: filters.manager.name },
		});
	}

	if (filters.roles?.length) {
		chips.push({
			id: "roles",
			type: "string",
			label: "Role",
			value: filters.roles.map((role) => role.name).join(", "),
		});
	}

	chips.push({
		id: "status",
		type: "string",
		label: "Assignment Status",
		value: STATUS_LABELS[resolveStatus(filters)],
	});

	if (filters.levelGap?.length) {
		chips.push({
			id: "levelGap",
			type: "string",
			label: "Level Gap",
			value: filters.levelGap.map((gap) => LEVEL_GAP_LABELS[gap]).join(", "),
		});
	}

	return chips;
};

// Chip ids equal CompetenciesFilter keys; returns the filters without the dismissed one,
// or null when the id isn't an active filter (nothing to remove).
export const removeFilter = (filters: CompetenciesFilter, id: string): CompetenciesFilter | null => {
	if (!(id in filters)) return null;
	const next = { ...filters };
	delete next[id as keyof CompetenciesFilter];
	return next;
};

// Status always renders a chip (see convertFiltersToFilterChips), so the badge always counts it,
// even at its default value — this keeps the badge equal to the number of chips rendered.
export const getActiveFiltersCount = (filters: CompetenciesFilter): number =>
	1 +
	(filters.assignee ? 1 : 0) +
	(filters.manager ? 1 : 0) +
	(filters.competency ? 1 : 0) +
	(filters.competencies?.length ? 1 : 0) +
	(filters.roles?.length ? 1 : 0) +
	(filters.source && filters.source !== "any" ? 1 : 0) +
	(filters.levelGap?.length ? 1 : 0);

export { getNonDismissibleChipIds };
